import React, { useRef, useLayoutEffect, useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import TextField from "@material-ui/core/TextField";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import GetAppIcon from "@material-ui/icons/GetApp";
import CodeRoundedIcon from "@material-ui/icons/CodeRounded";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import { i18n } from "../../translate/i18n";
import axios from "axios";
import toastError from "../../errors/toastError";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(3),
    paddingBottom: theme.spacing(4),
    overflowY: "auto",
    overflowX: "hidden",
    maxWidth: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    ...theme.scrollbarStyles,
  },
  title: {
    marginBottom: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  section: {
    marginTop: theme.spacing(3),
  },
  sectionTitle: {
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(1),
    fontWeight: 600,
  },
  codeBlock: {
    backgroundColor:
      theme.palette.type === "dark" ? "#2d2d2d" : "#f5f5f5",
    padding: theme.spacing(2),
    borderRadius: 8,
    fontFamily: "monospace",
    fontSize: "0.85rem",
    overflowX: "auto",
    wordBreak: "break-word",
    whiteSpace: "pre-wrap",
    borderLeft: `4px solid ${theme.palette.primary.main}`,
  },
  endpointItem: {
    paddingLeft: theme.spacing(2),
    "& .method": {
      fontWeight: 600,
      marginRight: theme.spacing(1),
      minWidth: 48,
      display: "inline-block",
    },
    "& .method.post": { color: "#2e7d32" },
    "& .method.get": { color: "#1565c0" },
  },
  downloadBtn: {
    marginTop: theme.spacing(2),
  },
  note: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.06)"
        : "rgba(0,0,0,0.04)",
    borderRadius: 8,
    fontSize: "0.9rem",
  },
  listRoot: {
    paddingTop: 0,
  },
  exampleBlock: {
    marginBottom: theme.spacing(2),
  },
  exampleLabel: {
    fontSize: "0.9rem",
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
    color: theme.palette.text.secondary,
  },
  testPanel: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(0,0,0,0.2)"
        : "rgba(0,0,0,0.03)",
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
  },
  testRow: {
    marginBottom: theme.spacing(2),
    "& .MuiFormControl-root": { minWidth: "100%" },
  },
  responseBlock: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: 8,
    fontFamily: "monospace",
    fontSize: "0.85rem",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    maxHeight: 320,
    overflowY: "auto",
    border: `1px solid ${theme.palette.divider}`,
  },
  responseSuccess: {
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(46, 125, 50, 0.15)"
        : "rgba(46, 125, 50, 0.08)",
    borderColor: "rgba(46, 125, 50, 0.5)",
  },
  responseError: {
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(211, 47, 47, 0.15)"
        : "rgba(211, 47, 47, 0.08)",
    borderColor: "rgba(211, 47, 47, 0.5)",
  },
  connectionBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.04)"
        : "rgba(0,0,0,0.025)",
  },
  connectionGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
    fontSize: "0.85rem",
    "& strong": {
      display: "block",
      color: theme.palette.text.secondary,
      fontWeight: 600,
    },
  },
}));

const getBaseUrl = () => {
  const url = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  const port = process.env.REACT_APP_PROXY_PORT;
  return port ? `${url}:${port}` : url;
};

const normalizeBrazilNumber = (number) => {
  const digits = String(number || "").replace(/\D/g, "");
  if (!digits) return "5511999998888";
  return digits.startsWith("55") ? digits : `55${digits}`;
};

const maskToken = (token) => {
  if (!token) return "";
  if (token.length <= 18) return token;
  return `${token.substring(0, 12)}...${token.substring(token.length - 6)}`;
};

const TEST_FORM_STORAGE_KEY = "apiOficialDocs.testForm";

const getStoredTestForm = () => {
  try {
    return JSON.parse(localStorage.getItem(TEST_FORM_STORAGE_KEY) || "{}");
  } catch (err) {
    return {};
  }
};
// Endpoints testáveis: key, method, path (pode ter :number), defaultBody (JSON string), needsNumberInPath
const TESTABLE_ENDPOINTS = [
  {
    key: "send/text",
    method: "POST",
    path: "/api/oficial/send/text",
    defaultBody: JSON.stringify(
      { number: "5511999998888", body: "Olá! Mensagem de teste.", previewUrl: false },
      null,
      2
    ),
    needsNumber: true,
  },
  {
    key: "send/template",
    method: "POST",
    path: "/api/oficial/send/template",
    defaultBody: JSON.stringify(
      {
        number: "5511999998888",
        templateName: "hello_world",
        language: "pt_BR",
        components: [
          {
            type: "body",
            parameters: [{ type: "text", text: "João" }],
          },
        ],
      },
      null,
      2
    ),
    needsNumber: true,
  },
  {
    key: "templates",
    method: "GET",
    path: "/api/oficial/templates",
    defaultBody: "",
    needsNumber: false,
  },
  {
    key: "media-limits",
    method: "GET",
    path: "/api/oficial/media-limits",
    defaultBody: "",
    needsNumber: false,
  },
  {
    key: "check-window",
    method: "GET",
    path: "/api/oficial/check-window",
    pathSuffix: "/:number",
    defaultBody: "",
    needsNumber: true,
  },
];

const ApiOficialDocs = () => {
  const classes = useStyles();
  const paperRef = useRef(null);
  const storedTestForm = getStoredTestForm();
  const [testToken, setTestToken] = useState(storedTestForm.token || "");
  const [testNumber, setTestNumber] = useState(storedTestForm.number || "5511999998888");
  const [testEndpointKey, setTestEndpointKey] = useState(storedTestForm.endpointKey || "send/text");
  const [testBody, setTestBody] = useState(
    storedTestForm.body || TESTABLE_ENDPOINTS[0].defaultBody
  );
  const [testLoading, setTestLoading] = useState(false);
  const [testResponse, setTestResponse] = useState(null);
  const [officialConnection, setOfficialConnection] = useState(null);
  const [connectionLoading, setConnectionLoading] = useState(true);

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    if (paperRef.current) paperRef.current.scrollTop = 0;
  }, []);
  useEffect(() => {
    localStorage.setItem(
      TEST_FORM_STORAGE_KEY,
      JSON.stringify({
        token: testToken,
        number: testNumber,
        endpointKey: testEndpointKey,
        body: testBody
      })
    );
  }, [testToken, testNumber, testEndpointKey, testBody]);

  useEffect(() => {
    const loadOfficialConnection = async () => {
      setConnectionLoading(true);
      try {
        const { data } = await api.get("/whatsapp/filter", {
          params: { channel: "whatsapp_oficial", session: 0 },
        });
        const connections = Array.isArray(data) ? data : [];
        const connection = connections.find((item) => item.channel === "whatsapp_oficial") || null;

        if (connection) {
          const token = connection.token || connection.send_token || "";
          const number = normalizeBrazilNumber(connection.phone_number);

          setOfficialConnection(connection);
          if (token) setTestToken(token);
          if (number) setTestNumber(number);
          setTestBody(JSON.stringify(
            { number, body: "Ola! Mensagem de teste.", previewUrl: false },
            null,
            2
          ));
        }
      } catch (err) {
        toastError(err);
      } finally {
        setConnectionLoading(false);
      }
    };

    loadOfficialConnection();
  }, []);

  const baseUrl = getBaseUrl();
  const postmanDownloadUrl = `${baseUrl}/api/oficial/postman-collection`;

  const selectedEndpoint = TESTABLE_ENDPOINTS.find((e) => e.key === testEndpointKey) || TESTABLE_ENDPOINTS[0];
  const configuredNumber = normalizeBrazilNumber(officialConnection?.phone_number);
  const configuredToken = officialConnection?.token || officialConnection?.send_token || "";

  const handleEndpointChange = (e) => {
    const key = e.target.value;
    setTestEndpointKey(key);
    const ep = TESTABLE_ENDPOINTS.find((x) => x.key === key);
    if (ep) setTestBody(ep.defaultBody.replaceAll("5511999998888", configuredNumber));
  };

  const handleSendTest = async () => {
    if (!testToken.trim()) {
      toastError({ message: i18n.t("apiOficialDocs.testToken") });
      return;
    }
    setTestLoading(true);
    setTestResponse(null);
    try {
      let url = `${baseUrl}${selectedEndpoint.path}`;
      if (selectedEndpoint.key === "check-window" && testNumber.trim()) {
        url = `${baseUrl}/api/oficial/check-window/${testNumber.trim()}`;
      }
      const config = {
        headers: {
          Authorization: `Bearer ${testToken.trim()}`,
          ...(selectedEndpoint.method === "POST" && testBody.trim()
            ? { "Content-Type": "application/json" }
            : {}),
        },
        timeout: 30000,
      };
      let res;
      if (selectedEndpoint.method === "GET") {
        res = await axios.get(url, config);
      } else {
        let body = {};
        if (testBody.trim()) {
          try {
            body = JSON.parse(testBody);
          } catch (e) {
            setTestResponse({
              status: 0,
              data: { error: "Body JSON inválido", message: e.message },
              isError: true,
            });
            setTestLoading(false);
            return;
          }
        }
        res = await axios.post(url, body, config);
      }
      setTestResponse({
        status: res.status,
        data: res.data,
        isError: false,
      });
    } catch (err) {
      const status = err.response?.status;
      const data = err.response?.data || { message: err.message };
      setTestResponse({
        status: status || 0,
        data,
        isError: true,
      });
    } finally {
      setTestLoading(false);
    }
  };

  const endpointGroups = [
    {
      title: i18n.t("apiOficialDocs.sections.send"),
      items: [
        { method: "POST", path: "/api/oficial/send/text", desc: "sendText" },
        { method: "POST", path: "/api/oficial/send/image", desc: "sendImage" },
        { method: "POST", path: "/api/oficial/send/audio", desc: "sendAudio" },
        { method: "POST", path: "/api/oficial/send/video", desc: "sendVideo" },
        { method: "POST", path: "/api/oficial/send/document", desc: "sendDocument" },
        { method: "POST", path: "/api/oficial/send/template", desc: "sendTemplate" },
      ],
    },
    {
      title: i18n.t("apiOficialDocs.sections.media"),
      items: [
        { method: "POST", path: "/api/oficial/upload-media", desc: "uploadMedia" },
        { method: "GET", path: "/api/oficial/media-limits", desc: "mediaLimits" },
      ],
    },
    {
      title: i18n.t("apiOficialDocs.sections.templates"),
      items: [
        { method: "GET", path: "/api/oficial/templates", desc: "listTemplates" },
        { method: "POST", path: "/api/oficial/validate-template", desc: "validateTemplate" },
      ],
    },
    {
      title: i18n.t("apiOficialDocs.sections.verification"),
      items: [
        { method: "GET", path: "/api/oficial/check-window/:number", desc: "checkWindow" },
      ],
    },
  ];

  const requestExamples = [
    {
      label: i18n.t("apiOficialDocs.exampleSendText"),
      method: "POST",
      path: "/api/oficial/send/text",
      body: `{
  "number": "${configuredNumber}",
  "body": "Olá! Como posso ajudar?",
  "previewUrl": false
}`,
    },
    {
      label: i18n.t("apiOficialDocs.exampleSendTemplate"),
      method: "POST",
      path: "/api/oficial/send/template",
      body: `{
  "number": "${configuredNumber}",
  "templateName": "hello_world",
  "language": "pt_BR",
  "components": [
    {
      "type": "body",
      "parameters": [{ "type": "text", "text": "João" }]
    }
  ]
}`,
    },
    {
      label: i18n.t("apiOficialDocs.exampleGetTemplates"),
      method: "GET",
      path: "/api/oficial/templates",
      body: null,
    },
  ];

  return (
    <Paper
      ref={paperRef}
      className={classes.mainPaper}
      style={{ marginLeft: "5px" }}
      variant="outlined"
    >
      <Typography variant="h5" className={classes.title}>
        <CodeRoundedIcon fontSize="large" />
        {i18n.t("apiOficialDocs.title")}
      </Typography>

      <Typography variant="body1" color="textSecondary">
        {i18n.t("apiOficialDocs.intro")}
      </Typography>

      <div className={classes.section}>
        <Typography variant="h6" className={classes.sectionTitle}>
          {i18n.t("apiOficialDocs.baseUrl")}
        </Typography>
        <div className={classes.codeBlock}>{baseUrl}</div>
      </div>

      <div className={classes.section}>
        <Typography variant="h6" className={classes.sectionTitle}>
          {i18n.t("apiOficialDocs.auth")}
        </Typography>
        <Typography variant="body2" paragraph>
          {i18n.t("apiOficialDocs.authDesc")}
        </Typography>
        <div className={classes.codeBlock}>
          {i18n.t("apiOficialDocs.authHeader")}
          {"\n"}Authorization: Bearer {configuredToken ? maskToken(configuredToken) : "<token_da_conexao_whatsapp_oficial>"}
        </div>
        <div className={classes.connectionBox}>
          <Typography variant="subtitle2">
            {connectionLoading
              ? "Carregando conexao WhatsApp Oficial..."
              : officialConnection
                ? "Conexao WhatsApp Oficial carregada do banco"
                : "Nenhuma conexao WhatsApp Oficial encontrada no banco"}
          </Typography>
          {officialConnection && (
            <div className={classes.connectionGrid}>
              <div>
                <strong>ID da conexao</strong>
                {officialConnection.id}
              </div>
              <div>
                <strong>Status</strong>
                {officialConnection.status || "-"}
              </div>
              <div>
                <strong>Numero remetente</strong>
                {configuredNumber}
              </div>
              <div>
                <strong>Phone Number ID</strong>
                {officialConnection.phone_number_id || "-"}
              </div>
              <div>
                <strong>WABA ID</strong>
                {officialConnection.waba_id || "-"}
              </div>
              <div>
                <strong>Business ID</strong>
                {officialConnection.business_id || "-"}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className={classes.section}>
        <Typography variant="h6" className={classes.sectionTitle}>
          {i18n.t("apiOficialDocs.examplesTitle")}
        </Typography>
        {requestExamples.map((ex) => (
          <div key={ex.path} className={classes.exampleBlock}>
            <div className={classes.exampleLabel}>
              {ex.method} {ex.path} — {ex.label}
            </div>
            <div className={classes.codeBlock}>
              {ex.method} {baseUrl}{ex.path}
              {"\n"}Authorization: Bearer {configuredToken ? maskToken(configuredToken) : "<token>"}
              {ex.body && (
                <>
                  {"\n\n"}Body (JSON):
                  {"\n"}
                  {ex.body}
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className={classes.section}>
        <Typography variant="h6" className={classes.sectionTitle}>
          {i18n.t("apiOficialDocs.testTitle")}
        </Typography>
        <Typography variant="body2" color="textSecondary" paragraph>
          {i18n.t("apiOficialDocs.testDesc")}
        </Typography>
        <div className={classes.testPanel}>
          <div className={classes.testRow}>
            <FormControl fullWidth size="small" variant="outlined">
              <InputLabel>{i18n.t("apiOficialDocs.testEndpoint")}</InputLabel>
              <Select
                value={testEndpointKey}
                onChange={handleEndpointChange}
                label={i18n.t("apiOficialDocs.testEndpoint")}
              >
                {TESTABLE_ENDPOINTS.map((ep) => (
                  <MenuItem key={ep.key} value={ep.key}>
                    {ep.method} {ep.path}
                    {ep.pathSuffix || ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div className={classes.testRow}>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              label={i18n.t("apiOficialDocs.testToken")}
              value={testToken}
              onChange={(e) => setTestToken(e.target.value)}
              type="password"
              placeholder="Token da conexão WhatsApp Oficial"
              helperText={officialConnection ? "Preenchido com o token da conexao configurada." : ""}
            />
          </div>
          {selectedEndpoint.needsNumber && (
            <div className={classes.testRow}>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                label={i18n.t("apiOficialDocs.testNumber")}
                value={testNumber}
                onChange={(e) => setTestNumber(e.target.value)}
                placeholder={configuredNumber}
                helperText="Este numero e o destinatario da mensagem."
              />
            </div>
          )}
          {(selectedEndpoint.method === "POST" && selectedEndpoint.defaultBody) && (
            <div className={classes.testRow}>
              <TextField
                fullWidth
                multiline
                rows={8}
                size="small"
                variant="outlined"
                label={i18n.t("apiOficialDocs.testBody")}
                value={testBody}
                onChange={(e) => setTestBody(e.target.value)}
                placeholder="{}"
                inputProps={{ style: { fontFamily: "monospace", fontSize: "0.85rem" } }}
              />
            </div>
          )}
          <Button
            variant="contained"
            color="primary"
            startIcon={testLoading ? null : <PlayArrowIcon />}
            disabled={testLoading}
            onClick={handleSendTest}
          >
            {testLoading ? i18n.t("apiOficialDocs.testLoading") : i18n.t("apiOficialDocs.testSend")}
          </Button>

          {testResponse && (
            <div className={classes.section}>
              <Typography variant="subtitle2" gutterBottom>
                {i18n.t("apiOficialDocs.testResponse")} — {i18n.t("apiOficialDocs.testStatus")}: {testResponse.status}
              </Typography>
              <div
                className={`${classes.responseBlock} ${
                  testResponse.isError ? classes.responseError : classes.responseSuccess
                }`}
              >
                {typeof testResponse.data === "object"
                  ? JSON.stringify(testResponse.data, null, 2)
                  : String(testResponse.data)}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className={classes.section}>
        <Typography variant="h6" className={classes.sectionTitle}>
          {i18n.t("apiOficialDocs.endpoints")}
        </Typography>
        {endpointGroups.map((group) => (
          <div key={group.title} style={{ marginBottom: 16 }}>
            <Typography variant="subtitle1" color="textSecondary" gutterBottom>
              {group.title}
            </Typography>
            <List className={classes.listRoot} dense>
              {group.items.map((item) => (
                <ListItem key={item.path} className={classes.endpointItem}>
                  <ListItemText
                    primary={
                      <span>
                        <span className={`method ${item.method.toLowerCase()}`}>
                          {item.method}
                        </span>
                        {item.path}
                      </span>
                    }
                    secondary={i18n.t(`apiOficialDocs.endpointsDesc.${item.desc}`)}
                  />
                </ListItem>
              ))}
            </List>
          </div>
        ))}
      </div>

      <div className={classes.section}>
        <Typography variant="h6" className={classes.sectionTitle}>
          {i18n.t("apiOficialDocs.postmanTitle")}
        </Typography>
        <Typography variant="body2" paragraph>
          {i18n.t("apiOficialDocs.postmanDesc")}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<GetAppIcon />}
          href={postmanDownloadUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={classes.downloadBtn}
        >
          {i18n.t("apiOficialDocs.downloadPostman")}
        </Button>
      </div>

      <div className={classes.note}>
        <Typography variant="subtitle2" gutterBottom>
          {i18n.t("apiOficialDocs.notesTitle")}
        </Typography>
        <ul style={{ margin: 0, paddingLeft: 20 }}>
          <li>{i18n.t("apiOficialDocs.note1")}</li>
          <li>{i18n.t("apiOficialDocs.note2")}</li>
          <li>{i18n.t("apiOficialDocs.note3")}</li>
        </ul>
      </div>
    </Paper>
  );
};

export default ApiOficialDocs;

